import type React from 'react';
import { useCallback } from 'react';
import { Pressable, StyleSheet, Text, TextInput, View } from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { tokens } from '../../design-system/tokens';
import { durationPresetLabel, setStatusStrings } from './strings';
import { STATUS_DURATION_PRESETS, type StatusDurationPreset } from './types';

export interface CustomDurationPickerProps {
  readonly preset: StatusDurationPreset;
  readonly customExpiry: Date;
  readonly now: Date;
  readonly onChangePreset: (preset: StatusDurationPreset) => void;
  readonly onChangeCustomExpiry: (date: Date) => void;
}

export const CustomDurationPicker: React.FC<CustomDurationPickerProps> = ({
  preset,
  customExpiry,
  now,
  onChangePreset,
  onChangeCustomExpiry,
}) => {
  const scheme = useColorScheme();
  const colorScheme = scheme === 'dark' ? 'dark' : 'light';
  const colors = tokens.color.semantic[colorScheme];

  const handleCustomInput = useCallback(
    (value: string) => {
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) {
        return;
      }
      const minMs = now.getTime() + 60_000;
      const maxMs = now.getTime() + 7 * 24 * 60 * 60 * 1000;
      const clamped = new Date(Math.max(minMs, Math.min(parsed.getTime(), maxMs)));
      onChangeCustomExpiry(clamped);
    },
    [now, onChangeCustomExpiry],
  );

  return (
    <View style={styles.container}>
      <Text style={[styles.heading, { color: colors.text.primary }]}>
        {setStatusStrings.durationLabel}
      </Text>
      <View style={styles.presetGrid}>
        {STATUS_DURATION_PRESETS.map((entry) => {
          const active = entry === preset;
          return (
            <Pressable
              key={entry}
              testID={`duration-preset-${entry}`}
              accessibilityRole="button"
              accessibilityState={{ selected: active }}
              accessibilityLabel={durationPresetLabel[entry]}
              onPress={() => onChangePreset(entry)}
              style={[
                styles.presetButton,
                { borderColor: active ? colors.action.primary : colors.border.primary },
                active && { backgroundColor: colors.action.primary },
              ]}
            >
              <Text
                style={[
                  styles.presetText,
                  { color: active ? colors.text.onAccent : colors.text.primary },
                ]}
              >
                {durationPresetLabel[entry]}
              </Text>
            </Pressable>
          );
        })}
      </View>

      {preset === 'custom' ? (
        <View testID="duration-custom-input">
          <Text style={[styles.heading, { color: colors.text.primary }]}>
            {setStatusStrings.customDurationLabel}
          </Text>
          <TextInput
            accessibilityLabel={setStatusStrings.customDurationLabel}
            placeholder="YYYY-MM-DDTHH:mm"
            placeholderTextColor={colors.text.tertiary}
            value={customExpiry.toISOString().slice(0, 16)}
            onChangeText={handleCustomInput}
            autoCapitalize="none"
            autoCorrect={false}
            style={[
              styles.input,
              {
                color: colors.text.primary,
                backgroundColor: colors.surface.secondary,
                borderColor: colors.border.primary,
              },
            ]}
          />
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: tokens.spacing.sm,
  },
  // Uniform with the shared `Input` label: footnote / weight-500 / primary.
  heading: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: tokens.typography.fontSize.footnote,
    fontWeight: '500',
  },
  presetGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.xs,
  },
  presetButton: {
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.xs,
    borderRadius: tokens.radius.pill,
    borderWidth: 1,
  },
  presetText: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: tokens.typography.fontSize.footnote,
    fontWeight: '600',
  },
  input: {
    minHeight: 40,
    paddingHorizontal: tokens.spacing.md,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: tokens.typography.fontSize.body,
  },
});
